import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Search, Crown, Phone, Mail, MapPin, Edit2, Trash2, Target, Filter, Users, Flag, X, SlidersHorizontal, List, Network } from 'lucide-react'
import clsx from 'clsx'
import axios from 'axios'
import { buildApiUrl } from '../../../config/api'
import { toast } from '../../../components/Toast'
import { Button, Badge, Card, Modal, ModalHeader, ModalBody, ModalFooter, EmptyState, SearchInput, SlidePanel, MultiSelect, PaginationBar } from '../../../components/ui'
import LeadersOrgChart from './LeadersOrgChart'
import { maskNumber, unmaskNumber, maskCPF, unmaskCPF, maskPhone, unmaskPhone, maskCEP, unmaskCEP, formatPhone } from '../../../utils/masks'
import { inputClass } from '../../../styles/forms'

interface Leader {
  id: number
  name: string
  cpf: string | null
  phone: string | null
  email: string | null
  photo_url: string | null
  status_code: string
  status_label: string
  city_name: string | null
  state_code: string | null
  neighborhood_name: string | null
  areas_count: number
  campaign_vote_goal?: number | null
  campaign_votes_obtained?: number | null
}

interface ActiveCampaign {
  id: number
  name: string
  is_active_campaign: boolean
}

export default function LeadersPage() {
  const [leaders, setLeaders] = useState<Leader[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(10)
  const [meta, setMeta] = useState({ total: 0, page: 1, per_page: 10, last_page: 1 })
  const [viewMode, setViewMode] = useState<'all' | 'campaign'>('all')
  const [displayMode, setDisplayMode] = useState<'list' | 'org'>('list')
  const [orgRefreshKey, setOrgRefreshKey] = useState(0)
  const [parentOptions, setParentOptions] = useState<{ value: string; label: string }[]>([])
  const [activeCampaign, setActiveCampaign] = useState<ActiveCampaign | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [selectedLeader, setSelectedLeader] = useState<Leader | null>(null)
  const [cepLoading, setCepLoading] = useState(false)
  const [states, setStates] = useState<{ id: number; code: string; name: string }[]>([])
  const [areaCities, setAreaCities] = useState<{ id: number; name: string }[]>([])
  const [areaStateId, setAreaStateId] = useState('')
  const [areaCityId, setAreaCityId] = useState('')
  const [areas, setAreas] = useState<{ geo_city_id: number; city_name: string; state_code: string }[]>([])
  const [filterPanelOpen, setFilterPanelOpen] = useState(false)
  const [filterCities, setFilterCities] = useState<{ id: number; name: string }[]>([])
  const [filterAreaCities, setFilterAreaCities] = useState<{ id: number; name: string }[]>([])
  const [filters, setFilters] = useState({ geo_state_id: '', geo_city_ids: [] as string[], area_state_id: '', area_city_ids: [] as string[], gender: '', has_user: '', can_register: '' })
  const [tempFilters, setTempFilters] = useState(filters)
  const navigate = useNavigate()

  const activeFilterCount = [
    filters.geo_city_ids.length > 0, filters.area_city_ids.length > 0, filters.gender, filters.has_user, filters.can_register,
  ].filter(Boolean).length

  // Form
  const [form, setForm] = useState({
    name: '', nickname: '', cpf: '', phone: '', email: '',
    birth_date: '', gender: '',
    zip_code: '', street: '', number: '', neighborhood: '', complement: '',
    geo_city_id: '', geo_neighborhood_id: '', city_display: '',
    notes: '',
    can_register_voters: false,
    parent_leader_id: '',
    add_to_campaign: false, campaign_vote_goal: '',
  })

  const fetchLeaders = useCallback(async () => {
    setLoading(true)
    try {
      const params: any = { search, page, per_page: perPage }
      if (viewMode === 'campaign' && activeCampaign) {
        params.campaign_id = activeCampaign.id
      }
      if (filters.geo_city_ids.length > 0) params.geo_city_id = filters.geo_city_ids.join(',')
      if (filters.area_city_ids.length > 0) params.area_city_id = filters.area_city_ids.join(',')
      if (filters.gender) params.gender = filters.gender
      if (filters.has_user) params.has_user = filters.has_user
      if (filters.can_register) params.can_register = filters.can_register
      const res = await axios.get(buildApiUrl('/v1/leaders'), { params })
      setLeaders(res.data.data)
      if (res.data.meta) setMeta(res.data.meta)
    } catch { /* */ } finally { setLoading(false) }
  }, [search, page, perPage, viewMode, activeCampaign, filters])

  useEffect(() => {
    // Buscar estados
    axios.get(buildApiUrl('/v1/geo/states'))
      .then(res => setStates(res.data.data))
      .catch(() => {})

    // Buscar campanha ativa
    axios.get(buildApiUrl('/v1/campaigns'))
      .then(res => {
        const active = res.data.data?.find((c: any) => c.is_active_campaign)
        setActiveCampaign(active || null)
      })
      .catch(() => {})
  }, [])

  useEffect(() => { fetchLeaders() }, [fetchLeaders])

  const initialized = useRef(false)
  useEffect(() => {
    if (!initialized.current) { initialized.current = true; return }
    setPage(1)
  }, [search, perPage, viewMode, filters])

  // Carregar cidades do filtro quando muda estado
  useEffect(() => {
    if (tempFilters.geo_state_id) {
      axios.get(buildApiUrl(`/v1/geo/states/${tempFilters.geo_state_id}/cities`))
        .then(r => setFilterCities(r.data.data))
        .catch(() => setFilterCities([]))
    } else {
      setFilterCities([])
    }
    setTempFilters(f => ({ ...f, geo_city_ids: [] }))
  }, [tempFilters.geo_state_id])

  // Carregar cidades do filtro de área quando muda estado
  useEffect(() => {
    if (tempFilters.area_state_id) {
      axios.get(buildApiUrl(`/v1/geo/states/${tempFilters.area_state_id}/cities`))
        .then(r => setFilterAreaCities(r.data.data))
        .catch(() => setFilterAreaCities([]))
    } else {
      setFilterAreaCities([])
    }
    setTempFilters(f => ({ ...f, area_city_ids: [] }))
  }, [tempFilters.area_state_id])

  // Carregar cidades quando seleciona estado na área
  useEffect(() => {
    if (areaStateId) {
      axios.get(buildApiUrl(`/v1/geo/states/${areaStateId}/cities`))
        .then(res => setAreaCities(res.data.data))
        .catch(() => setAreaCities([]))
    } else {
      setAreaCities([])
    }
    setAreaCityId('')
  }, [areaStateId])

  const loadParentOptions = useCallback(async (excludeId?: number) => {
    try {
      const res = await axios.get(buildApiUrl('/v1/leaders'), { params: { per_page: 100 } })
      const opts = (res.data.data as Leader[])
        .filter(l => l.id !== excludeId)
        .map(l => ({ value: String(l.id), label: (l as any).nickname ? `${l.name} (${(l as any).nickname})` : l.name }))
      setParentOptions(opts)
    } catch { setParentOptions([]) }
  }, [])

  function openCreate() {
    setSelectedLeader(null)
    setForm({
      name: '', nickname: '', cpf: '', phone: '', email: '',
      birth_date: '', gender: '',
      zip_code: '', street: '', number: '', neighborhood: '', complement: '',
      geo_city_id: '', geo_neighborhood_id: '', city_display: '',
      notes: '',
      can_register_voters: false,
      parent_leader_id: '',
      add_to_campaign: !!activeCampaign, campaign_vote_goal: '',
    })
    setAreas([])
    setAreaStateId('')
    setAreaCityId('')
    loadParentOptions()
    setModalOpen(true)
  }

  // Abre o cadastro já com uma liderança responsável (pai) pré-selecionada — usado pelo "+" do organograma
  function openCreateWithParent(parentLeaderId: number | null) {
    setSelectedLeader(null)
    setForm({
      name: '', nickname: '', cpf: '', phone: '', email: '',
      birth_date: '', gender: '',
      zip_code: '', street: '', number: '', neighborhood: '', complement: '',
      geo_city_id: '', geo_neighborhood_id: '', city_display: '',
      notes: '',
      can_register_voters: false,
      parent_leader_id: parentLeaderId ? String(parentLeaderId) : '',
      add_to_campaign: !!activeCampaign, campaign_vote_goal: '',
    })
    setAreas([])
    setAreaStateId('')
    setAreaCityId('')
    // Não excluir o pai da lista — ele precisa aparecer selecionado no campo
    loadParentOptions()
    setModalOpen(true)
  }

  function addArea() {
    if (!areaCityId) return
    const city = areaCities.find(c => c.id === parseInt(areaCityId))
    const state = states.find(s => s.id === parseInt(areaStateId))
    if (!city || !state) return
    if (areas.some(a => a.geo_city_id === city.id)) return
    setAreas([...areas, { geo_city_id: city.id, city_name: city.name, state_code: state.code }])
    setAreaCityId('')
  }

  function removeArea(cityId: number) {
    setAreas(areas.filter(a => a.geo_city_id !== cityId))
  }

  async function openEdit(leader: Leader) {
    try {
      const res = await axios.get(buildApiUrl(`/v1/leaders/${leader.id}`))
      const l = res.data.data.leader
      const leaderAreas = res.data.data.areas || []
      const leaderCampaigns = res.data.data.campaigns || []

      // Verificar se está na campanha ativa
      const activeCampaignLink = leaderCampaigns.find((c: any) => c.is_active_campaign == 1 && c.is_active == 1)

      setSelectedLeader(leader)
      setForm({
        name: l.name || '',
        nickname: l.nickname || '',
        cpf: l.cpf ? maskCPF(l.cpf) : '',
        phone: l.phone ? maskPhone(l.phone) : '',
        email: l.email || '',
        birth_date: l.birth_date || '',
        gender: l.gender || '',
        zip_code: l.zip_code ? maskCEP(l.zip_code) : '',
        street: l.street || '',
        number: l.number || '',
        neighborhood: l.neighborhood || '',
        complement: l.complement || '',
        geo_city_id: l.geo_city_id?.toString() || '',
        geo_neighborhood_id: l.geo_neighborhood_id?.toString() || '',
        city_display: l.city_name && l.state_code ? `${l.city_name}/${l.state_code}` : '',
        notes: l.notes || '',
        can_register_voters: !!l.can_register_voters,
        parent_leader_id: l.parent_leader_id ? String(l.parent_leader_id) : '',
        add_to_campaign: !!activeCampaignLink,
        campaign_vote_goal: activeCampaignLink?.vote_goal ? maskNumber(String(activeCampaignLink.vote_goal)) : '',
      })
      setAreas(leaderAreas.map((a: any) => ({ geo_city_id: a.geo_city_id, city_name: a.city_name, state_code: a.state_code })))
      setAreaStateId('')
      setAreaCityId('')
      loadParentOptions(leader.id)
      setModalOpen(true)
    } catch {
      toast('error', 'Erro ao carregar liderança')
    }
  }

  function openDelete(leader: Leader) {
    setSelectedLeader(leader)
    setDeleteModalOpen(true)
  }

  async function handleCepLookup() {
    const cep = unmaskCEP(form.zip_code)
    if (cep.length < 8) return
    setCepLoading(true)
    try {
      const res = await axios.get(buildApiUrl(`/v1/geo/cep/${cep}`))
      const d = res.data.data
      setForm(f => ({
        ...f,
        street: d.street || f.street,
        neighborhood: d.neighborhood || f.neighborhood,
        geo_city_id: d.geo_city_id?.toString() || f.geo_city_id,
        geo_neighborhood_id: d.geo_neighborhood_id?.toString() || f.geo_neighborhood_id,
        city_display: d.city_name && d.state_code ? `${d.city_name}/${d.state_code}` : f.city_display,
      }))
    } catch { /* */ } finally {
      setCepLoading(false)
    }
  }

  async function handleSave() {
    if (saving) return
    setSaving(true)
    const payload: any = {
      name: form.name,
      nickname: form.nickname || null,
      cpf: unmaskCPF(form.cpf) || null,
      phone: unmaskPhone(form.phone) || null,
      email: form.email || null,
      birth_date: form.birth_date || null,
      gender: form.gender || null,
      zip_code: unmaskCEP(form.zip_code) || null,
      street: form.street || null,
      number: form.number || null,
      neighborhood: form.neighborhood || null,
      complement: form.complement || null,
      geo_city_id: form.geo_city_id ? parseInt(form.geo_city_id) : null,
      geo_neighborhood_id: form.geo_neighborhood_id ? parseInt(form.geo_neighborhood_id) : null,
      notes: form.notes || null,
      can_register_voters: form.can_register_voters,
      parent_leader_id: form.parent_leader_id ? parseInt(form.parent_leader_id) : null,
      add_to_campaign: form.add_to_campaign,
      campaign_vote_goal: unmaskNumber(form.campaign_vote_goal),
    }

    try {
      if (selectedLeader) {
        // Editar
        await axios.put(buildApiUrl(`/v1/leaders/${selectedLeader.id}`), payload)

        // Sincronizar áreas
        const resDetail = await axios.get(buildApiUrl(`/v1/leaders/${selectedLeader.id}`))
        const existingAreas = resDetail.data.data.areas || []
        for (const ea of existingAreas) {
          if (!areas.some(a => Number(a.geo_city_id) === Number(ea.geo_city_id))) {
            await axios.delete(buildApiUrl(`/v1/leaders/${selectedLeader.id}/areas/${ea.id}`))
          }
        }
        for (const a of areas) {
          if (!existingAreas.some((ea: any) => Number(ea.geo_city_id) === Number(a.geo_city_id))) {
            await axios.post(buildApiUrl(`/v1/leaders/${selectedLeader.id}/areas`), { geo_city_id: a.geo_city_id })
          }
        }

        // Sincronizar campanha
        if (activeCampaign && form.add_to_campaign) {
          await axios.post(buildApiUrl(`/v1/leaders/${selectedLeader.id}/campaign`), {
            campaign_id: activeCampaign.id,
            vote_goal: unmaskNumber(form.campaign_vote_goal),
          }).catch(() => {}) // ignora se já existe
        }

        toast('success', 'Liderança atualizada')
      } else {
        // Criar
        const res = await axios.post(buildApiUrl('/v1/leaders'), payload)
        const leaderId = res.data.data.id

        for (const area of areas) {
          await axios.post(buildApiUrl(`/v1/leaders/${leaderId}/areas`), { geo_city_id: area.geo_city_id })
        }

        toast('success', 'Liderança cadastrada')
      }
      setModalOpen(false)
      fetchLeaders()
      setOrgRefreshKey(k => k + 1)
    } catch (err: any) {
      toast('error', err.response?.data?.error || err.response?.data?.message || 'Erro ao salvar')
    } finally { setSaving(false) }
  }

  async function handleDelete() {
    if (!selectedLeader) return
    try {
      await axios.delete(buildApiUrl(`/v1/leaders/${selectedLeader.id}`))
      toast('success', 'Liderança desativada')
      setDeleteModalOpen(false)
      fetchLeaders()
    } catch { toast('error', 'Erro ao desativar') }
  }



  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Lideranças</h1>
          <p className="text-gray-500 mt-1">Gerencie lideranças estratégicas</p>
        </div>
        <div className="flex items-center gap-2">
          {activeCampaign && (
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              <button onClick={() => setViewMode('all')} className={clsx('px-3 py-1.5 rounded-md text-sm font-medium transition-colors', viewMode === 'all' ? 'bg-brand-50 text-brand-600 shadow-xs' : 'text-gray-500')}>
                Todos
              </button>
              <button onClick={() => setViewMode('campaign')} className={clsx('px-3 py-1.5 rounded-md text-sm font-medium transition-colors', viewMode === 'campaign' ? 'bg-brand-50 text-brand-600 shadow-xs' : 'text-gray-500')}>
                Campanha atual
              </button>
            </div>
          )}
          <Button icon={<Plus className="w-4 h-4" />} onClick={openCreate}>Nova Liderança</Button>
        </div>
      </div>

      {/* Toolbar: modo (esquerda) + busca/filtros (só na lista) */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 flex-shrink-0">
          <button onClick={() => setDisplayMode('list')} className={clsx('flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors', displayMode === 'list' ? 'bg-white text-brand-600 shadow-xs' : 'text-gray-500 hover:text-gray-700')}>
            <List className="w-4 h-4" /> Lista
          </button>
          <button onClick={() => setDisplayMode('org')} className={clsx('flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors', displayMode === 'org' ? 'bg-white text-brand-600 shadow-xs' : 'text-gray-500 hover:text-gray-700')}>
            <Network className="w-4 h-4" /> Organograma
          </button>
        </div>

        <div className="flex-1">
          <SearchInput
            placeholder="Buscar por nome, apelido, telefone, email, CPF..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        {(
        <button
          onClick={() => { setTempFilters(filters); setFilterPanelOpen(true) }}
          className={clsx(
            'h-11 px-4 flex items-center gap-2 rounded-lg border text-sm font-medium transition-colors',
            activeFilterCount > 0
              ? 'bg-brand-50 border-brand-200 text-brand-600'
              : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
          )}
        >
          <SlidersHorizontal className="w-4 h-4" />
          Filtros
          {activeFilterCount > 0 && (
            <span className="ml-1 bg-brand-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">{activeFilterCount}</span>
          )}
        </button>
        )}
      </div>

      {displayMode === 'org' ? (
        <LeadersOrgChart
          campaignId={viewMode === 'campaign' && activeCampaign ? activeCampaign.id : null}
          search={search}
          filters={filters}
          onSelectLeader={id => navigate(`/leaders/${id}`)}
          onAddChild={parentId => openCreateWithParent(parentId)}
          refreshKey={orgRefreshKey}
        />
      ) : (
      <>
      {/* Lista */}
      {loading ? (
        <Card padding="none">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Telefone</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cidade</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acesso</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {[1,2,3,4,5].map(i => (
                  <tr key={i}>
                    <td className="px-4 py-3.5"><div className="h-4 w-10 bg-gray-100 rounded animate-pulse" /></td>
                    <td className="px-4 py-3.5"><div className="h-4 w-40 bg-gray-100 rounded animate-pulse" /></td>
                    <td className="px-4 py-3.5"><div className="h-4 w-32 bg-gray-100 rounded animate-pulse" /></td>
                    <td className="px-4 py-3.5"><div className="h-4 w-28 bg-gray-100 rounded animate-pulse" /></td>
                    <td className="px-4 py-3.5"><div className="h-5 w-16 bg-gray-100 rounded-full animate-pulse" /></td>
                    <td className="px-4 py-3.5"><div className="h-4 w-16 bg-gray-100 rounded animate-pulse ml-auto" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : leaders.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Crown className="w-7 h-7" />}
            title={viewMode === 'campaign' ? 'Nenhuma liderança na campanha' : 'Nenhuma liderança cadastrada'}
            description={viewMode === 'campaign' ? 'Adicione lideranças à campanha ativa.' : 'Cadastre lideranças estratégicas para fortalecer sua rede.'}
            action={<Button size="sm" onClick={openCreate} icon={<Plus className="w-4 h-4" />}>Cadastrar</Button>}
          />
        </Card>
      ) : (
        <Card padding="none">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Telefone</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cidade</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acesso</th>
                  {viewMode === 'campaign' && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Meta</th>
                  )}
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {leaders.map(leader => (
                  <tr key={leader.id} className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => navigate(`/leaders/${leader.id}`)}>
                    <td className="px-4 py-3.5">
                      <span className="text-xs font-mono font-medium text-gray-400">#{leader.id}</span>
                    </td>
                    <td className="px-4 py-3.5">
                      <p className="text-sm font-medium text-gray-900">{leader.name}</p>
                      {(leader as any).nickname && (
                        <p className="text-xs text-gray-400">{(leader as any).nickname}</p>
                      )}
                    </td>
                    <td className="px-4 py-3.5 text-sm text-gray-700">
                      {leader.phone ? formatPhone(leader.phone) : '—'}
                    </td>
                    <td className="px-4 py-3.5 text-sm text-gray-700">
                      {leader.city_name ? `${leader.city_name}/${leader.state_code}` : '—'}
                    </td>
                    <td className="px-4 py-3.5">
                      <Badge variant={(leader as any).user_id ? 'success' : 'gray'} dot size="sm">{(leader as any).user_id ? 'Com acesso' : 'Sem acesso'}</Badge>
                    </td>
                    {viewMode === 'campaign' && (
                      <td className="px-4 py-3.5 text-sm">
                        {leader.campaign_vote_goal ? (
                          <span className="font-medium text-gray-800">{leader.campaign_vote_goal.toLocaleString('pt-BR')}</span>
                        ) : '—'}
                      </td>
                    )}
                    <td className="px-4 py-3.5 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={e => { e.stopPropagation(); openEdit(leader) }} className="h-8 w-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={e => { e.stopPropagation(); openDelete(leader) }} className="h-8 w-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-error-500 hover:bg-error-50 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <PaginationBar page={page} perPage={perPage} total={meta.total} lastPage={meta.last_page} onPageChange={setPage} onPerPageChange={n => { setPerPage(n); setPage(1) }} />
        </Card>
      )}
      </>
      )}

      {/* Modal criar */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} size="lg">
        <ModalHeader onClose={() => setModalOpen(false)}>{selectedLeader ? 'Editar Liderança' : 'Nova Liderança'}</ModalHeader>
        <ModalBody>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Nome completo <span className="text-error-500">*</span></label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Nome da liderança"
                  className="h-11 w-full rounded-lg border border-gray-300 px-4 text-sm shadow-xs placeholder:text-gray-400 focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10 outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Apelido</label>
                <input value={form.nickname} onChange={e => setForm({ ...form, nickname: e.target.value })} placeholder="Como é conhecido"
                  className="h-11 w-full rounded-lg border border-gray-300 px-4 text-sm shadow-xs placeholder:text-gray-400 focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10 outline-none" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">CPF <span className="text-error-500">*</span></label>
                <input value={form.cpf} onChange={e => setForm({ ...form, cpf: maskCPF(e.target.value) })} placeholder="000.000.000-00"
                  className="h-11 w-full rounded-lg border border-gray-300 px-4 text-sm shadow-xs placeholder:text-gray-400 focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10 outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Telefone <span className="text-error-500">*</span></label>
                <input value={form.phone} onChange={e => setForm({ ...form, phone: maskPhone(e.target.value) })} placeholder="(82) 99999-9999"
                  className="h-11 w-full rounded-lg border border-gray-300 px-4 text-sm shadow-xs placeholder:text-gray-400 focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10 outline-none" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
              <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="email@exemplo.com"
                className="h-11 w-full rounded-lg border border-gray-300 px-4 text-sm shadow-xs placeholder:text-gray-400 focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10 outline-none" />
            </div>

            {/* Liderança responsável (pai) — hierarquia/organograma */}
            <div>
              <MultiSelect
                label="Liderança responsável (opcional)"
                options={parentOptions}
                selected={form.parent_leader_id ? [form.parent_leader_id] : []}
                onChange={v => setForm({ ...form, parent_leader_id: v[v.length - 1] || '' })}
                placeholder="Nenhuma (responde direto ao candidato)"
              />
              <p className="text-xs text-gray-400 mt-1">Define quem esta liderança responde no organograma.</p>
            </div>

            {/* Endereço */}
            <div className="border-t border-gray-100 pt-4">
              <p className="text-sm font-medium text-gray-700 mb-3">Endereço <span className="text-error-500">*</span></p>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">CEP</label>
                  <div className="relative">
                    <input value={form.zip_code} onChange={e => setForm({ ...form, zip_code: maskCEP(e.target.value) })} onBlur={handleCepLookup} placeholder="00000-000"
                      className="h-11 w-full rounded-lg border border-gray-300 px-4 pr-10 text-sm shadow-xs placeholder:text-gray-400 focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10 outline-none" />
                    {cepLoading && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <div className="w-4 h-4 border-2 border-brand-200 border-t-brand-500 rounded-full animate-spin" />
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-span-2">
                  <label className="block text-xs text-gray-500 mb-1">Rua</label>
                  <input value={form.street} onChange={e => setForm({ ...form, street: e.target.value })}
                    className="h-11 w-full rounded-lg border border-gray-300 px-4 text-sm shadow-xs focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10 outline-none" />
                </div>
              </div>
              <div className="grid grid-cols-4 gap-4 mt-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Número</label>
                  <input value={form.number} onChange={e => setForm({ ...form, number: e.target.value })}
                    className="h-11 w-full rounded-lg border border-gray-300 px-4 text-sm shadow-xs focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10 outline-none" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Bairro</label>
                  <input value={form.neighborhood} onChange={e => setForm({ ...form, neighborhood: e.target.value })}
                    className="h-11 w-full rounded-lg border border-gray-300 px-4 text-sm shadow-xs focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10 outline-none" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Complemento</label>
                  <input value={form.complement} onChange={e => setForm({ ...form, complement: e.target.value })}
                    className="h-11 w-full rounded-lg border border-gray-300 px-4 text-sm shadow-xs focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10 outline-none" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Cidade/UF</label>
                  <input value={form.city_display} readOnly placeholder="Preenchido pelo CEP"
                    className="h-11 w-full rounded-lg border border-gray-200 bg-gray-50 px-4 text-sm text-gray-600 cursor-not-allowed" />
                </div>
              </div>
            </div>

            {/* Áreas de atuação */}
            <div className="border-t border-gray-100 pt-4">
              <p className="text-sm font-medium text-gray-700 mb-3">Áreas de atuação</p>
              <div className="flex gap-2 mb-3">
                <select value={areaStateId} onChange={e => setAreaStateId(e.target.value)}
                  className="h-10 rounded-lg border border-gray-300 px-3 text-sm shadow-xs focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10 outline-none appearance-none">
                  <option value="">Estado</option>
                  {states.map(s => <option key={s.id} value={s.id}>{s.code}</option>)}
                </select>
                <select value={areaCityId} onChange={e => setAreaCityId(e.target.value)} disabled={!areaStateId}
                  className="h-10 flex-1 rounded-lg border border-gray-300 px-3 text-sm shadow-xs focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10 outline-none appearance-none disabled:opacity-50">
                  <option value="">{areaStateId ? 'Selecione a cidade' : 'Selecione o estado'}</option>
                  {areaCities.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <button type="button" onClick={addArea} disabled={!areaCityId}
                  className="h-10 px-3 rounded-lg bg-brand-50 text-brand-500 hover:bg-brand-100 disabled:opacity-30 transition-colors">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              {areas.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {areas.map(a => (
                    <span key={a.geo_city_id} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 rounded-lg text-sm text-gray-700">
                      <MapPin className="w-3 h-3 text-gray-400" />
                      {a.city_name}/{a.state_code}
                      <button type="button" onClick={() => removeArea(a.geo_city_id)} className="text-gray-400 hover:text-error-500">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Observações</label>
              <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="Anotações sobre a liderança..."
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm shadow-xs placeholder:text-gray-400 focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10 outline-none resize-none" />
            </div>

            {/* Apto a cadastrar */}
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => setForm({ ...form, can_register_voters: !form.can_register_voters })}>
              <div className={clsx('w-10 h-6 rounded-full transition-colors relative', form.can_register_voters ? 'bg-brand-500' : 'bg-gray-300')}>
                <span className={clsx('absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-xs transition-transform', form.can_register_voters ? 'left-[18px]' : 'left-0.5')} />
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">Apto a cadastrar contatos</span>
                <p className="text-xs text-gray-400">Indica que esta lideranca pode registrar novos contatos</p>
              </div>
            </div>

            {/* Campanha */}
            {activeCampaign && (
              <div className="border-t border-gray-100 pt-4">
                <div className="flex items-center gap-3 p-4 rounded-xl bg-brand-25 border border-brand-100 mb-3 cursor-pointer" onClick={() => setForm({ ...form, add_to_campaign: !form.add_to_campaign })}>
                  <input type="checkbox" checked={form.add_to_campaign} onChange={() => {}}
                    className="w-4 h-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Flag className="w-4 h-4 text-brand-500" />
                      <span className="text-sm font-medium text-gray-800">Adicionar à campanha ativa</span>
                    </div>
                    <p className="text-xs text-brand-500 mt-0.5">{activeCampaign.name}</p>
                  </div>
                </div>

                {form.add_to_campaign && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Meta de votos para esta liderança</label>
                    <input type="text" inputMode="numeric" value={form.campaign_vote_goal} onChange={e => setForm({ ...form, campaign_vote_goal: maskNumber(e.target.value) })} placeholder="5.000"
                      className="h-11 w-full rounded-lg border border-gray-300 px-4 text-sm shadow-xs placeholder:text-gray-400 focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10 outline-none" />
                  </div>
                )}
              </div>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
          <Button onClick={handleSave} disabled={!form.name || !form.cpf || !form.phone || !form.zip_code} loading={saving}>{selectedLeader ? 'Salvar' : 'Cadastrar'}</Button>
        </ModalFooter>
      </Modal>

      {/* Modal excluir */}
      <Modal open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} size="sm">
        <ModalHeader onClose={() => setDeleteModalOpen(false)}>Desativar Liderança</ModalHeader>
        <ModalBody>
          <p className="text-sm text-gray-600">Deseja desativar <strong>"{selectedLeader?.name}"</strong>? A liderança será marcada como inativa e poderá ser reativada depois.</p>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>Cancelar</Button>
          <Button variant="danger" onClick={handleDelete}>Desativar</Button>
        </ModalFooter>
      </Modal>

      {/* Painel filtros */}
      <SlidePanel open={filterPanelOpen} onClose={() => setFilterPanelOpen(false)} title="Filtros" size="md">
        <div className="p-6 space-y-5">
          {/* Endereço do líder */}
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Endereço do líder</p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Estado</label>
            <select value={tempFilters.geo_state_id} onChange={e => setTempFilters({ ...tempFilters, geo_state_id: e.target.value })} className={clsx(inputClass, 'appearance-none')}>
              <option value="">Todos</option>
              {states.map(s => <option key={s.id} value={s.id}>{s.name} ({s.code})</option>)}
            </select>
          </div>
          <MultiSelect
            label="Cidade"
            options={filterCities.map(c => ({ value: String(c.id), label: c.name }))}
            selected={tempFilters.geo_city_ids}
            onChange={v => setTempFilters({ ...tempFilters, geo_city_ids: v })}
            placeholder={tempFilters.geo_state_id ? 'Todas as cidades' : 'Selecione o estado'}
          />

          {/* Área de atuação */}
          <div className="border-t border-gray-100 pt-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Área de atuação</p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Estado</label>
                <select value={tempFilters.area_state_id} onChange={e => setTempFilters({ ...tempFilters, area_state_id: e.target.value })} className={clsx(inputClass, 'appearance-none')}>
                  <option value="">Todos</option>
                  {states.map(s => <option key={s.id} value={s.id}>{s.name} ({s.code})</option>)}
                </select>
              </div>
              <MultiSelect
                label="Cidade"
                options={filterAreaCities.map(c => ({ value: String(c.id), label: c.name }))}
                selected={tempFilters.area_city_ids}
                onChange={v => setTempFilters({ ...tempFilters, area_city_ids: v })}
                placeholder={tempFilters.area_state_id ? 'Todas as cidades' : 'Selecione o estado'}
              />
            </div>
          </div>

          {/* Outros filtros */}
          <div className="border-t border-gray-100 pt-4 space-y-4">
            <MultiSelect
              label="Gênero"
              options={[{ value: 'M', label: 'Masculino' }, { value: 'F', label: 'Feminino' }, { value: 'O', label: 'Outro' }]}
              selected={tempFilters.gender ? [tempFilters.gender] : []}
              onChange={v => setTempFilters({ ...tempFilters, gender: v[v.length - 1] || '' })}
              placeholder="Todos"
              searchable={false}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Acesso ao sistema</label>
              <select value={tempFilters.has_user} onChange={e => setTempFilters({ ...tempFilters, has_user: e.target.value })} className={clsx(inputClass, 'appearance-none')}>
                <option value="">Todos</option>
                <option value="1">Com acesso</option>
                <option value="0">Sem acesso</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Apto a cadastrar</label>
              <select value={tempFilters.can_register} onChange={e => setTempFilters({ ...tempFilters, can_register: e.target.value })} className={clsx(inputClass, 'appearance-none')}>
                <option value="">Todos</option>
                <option value="1">Sim</option>
                <option value="0">Não</option>
              </select>
            </div>
          </div>

          <div className="pt-4 flex gap-3">
            <Button onClick={() => { setFilters(tempFilters); setFilterPanelOpen(false) }} className="flex-1">Aplicar filtros</Button>
            <Button variant="outline" onClick={() => { const empty = { geo_state_id: '', geo_city_ids: [] as string[], area_state_id: '', area_city_ids: [] as string[], gender: '', has_user: '', can_register: '' }; setTempFilters(empty); setFilters(empty); setFilterPanelOpen(false) }} className="flex-1">Limpar</Button>
          </div>
        </div>
      </SlidePanel>
    </div>
  )
}
